import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useState, type ReactNode } from 'react';

const variants = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
  exit: {
    opacity: 0,
    y: -6,
    transition: { duration: 0.15, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

export function PageTransition({ children, keyValue }: { children: ReactNode; keyValue?: string }) {
  const { pathname } = useLocation();
  const logicalKey = keyValue ?? pathname;

  // AnimatePresence's exit bookkeeping is keyed by this string. If the same
  // logical key reappears after a full exit+enter cycle (e.g. navigating away
  // from a page and back to it) within the same long-lived AnimatePresence
  // instance, the revived element can get stuck applying its exit styles
  // instead of animating in. A nonce that only bumps when the logical key
  // actually changes keeps "same section = no re-animate" while guaranteeing
  // every mount gets a key AnimatePresence has never seen before.
  const [renderKey, setRenderKey] = useState({ logicalKey, nonce: 0 });
  if (renderKey.logicalKey !== logicalKey) {
    setRenderKey({ logicalKey, nonce: renderKey.nonce + 1 });
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={`${logicalKey}-${renderKey.nonce}`}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ height: '100%' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
